import { useCallback } from 'react'
import { useThreadContext } from '../context/ThreadProvider'
import { stopThread as stopThreadService } from '../services/stopThread'

// Track if we've already warned to avoid spam
let hasWarned = false

/**
 * @deprecated Use `const { stopExecution } = useThread()` instead.
 *
 * Hook that returns a function to stop the current thread's execution.
 * Must be used within a ThreadProvider.
 *
 * This hook automatically uses the thread ID from the ThreadProvider context,
 * so you don't need to pass it manually.
 *
 * @returns A function that stops the current thread's execution
 *
 * @throws Error if used outside of ThreadProvider
 *
 * @example
 * ```tsx
 * // NEW - Recommended
 * const { stopExecution } = useThread()
 * await stopExecution()
 *
 * // OLD - Deprecated
 * const stopThread = useStopThread()
 * await stopThread()
 * ```
 */
export function useStopThread(): () => Promise<void> {
  if (!hasWarned && process.env.NODE_ENV !== 'production') {
    hasWarned = true
    console.warn(
      '[DEPRECATED] useStopThread() is deprecated.\n' +
      'Use: const { stopExecution } = useThread()'
    )
  }
  let context: ReturnType<typeof useThreadContext>

  try {
    context = useThreadContext()
  } catch {
    throw new Error('useStopThread must be used within a ThreadProvider')
  }

  const { threadId } = context

  return useCallback(
    () => {
      return stopThreadService(threadId)
    },
    [threadId]
  )
}
